import { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from './Sidebar';

import {
  Bell,
  ChevronDown,
  LogOut
} from 'lucide-react';

const PAGE_TITLES = {
  '/admin': {
    title: 'Dashboard',
    sub: 'Welcome back to Zim CSO Database'
  },

  '/admin/organisations': {
    title: 'Organisations (CSOs)',
    sub: 'Manage, search and explore Civil Society Organisations across Zimbabwe.'
  },

  '/admin/organisations/new': {
    title: 'Add Organisation',
    sub: 'Register a new Civil Society Organisation'
  },

  '/admin/users': {
    title: 'User Management',
    sub: 'Manage ZAN staff accounts and access levels'
  },

  '/admin/provinces': {
    title: 'Provinces',
    sub: "Manage Zimbabwe's 10 provinces"
  },

  '/admin/districts': {
    title: 'Districts',
    sub: 'Manage districts within each province'
  },

  '/admin/thematic': {
    title: 'Thematic Areas',
    sub: 'Manage health thematic area categories'
  },

  '/admin/news': {
    title: 'News & Updates',
    sub: 'Publish news and announcements'
  },

  '/admin/resources': {
    title: 'Resources',
    sub: 'Manage downloadable resources and documents'
  },

  '/admin/media': {
    title: 'Media Library',
    sub: 'Browse and manage all uploaded media files'
  },

  '/admin/reports': {
    title: 'Reports & Analytics',
    sub: 'Data insights and visualisations'
  },

  '/admin/audit': {
    title: 'Audit Logs',
    sub: 'Track all system activity and changes'
  },

  '/admin/settings': {
    title: 'Settings',
    sub: 'Configure site settings and preferences'
  },

  '/admin/contact': {
    title: 'Contact Inbox',
    sub: 'Messages submitted via the contact form'
  }
};

export default function AdminLayout({ children }) {

  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const pageKey = Object.keys(PAGE_TITLES).find((k) => {
    if (k === '/admin') return pathname === '/admin';
    return pathname.startsWith(k);
  });

  const { title, sub } =
    PAGE_TITLES[pageKey] || {
      title: 'Admin Panel',
      sub: ''
    };

  const isEdit = pathname.includes('/edit/');

  const isDetail =
    pathname.match(/\/admin\/organisations\/[a-f0-9-]{36}$/) &&
    !pathname.includes('/edit');

  const finalTitle = isEdit
    ? 'Edit Organisation'
    : isDetail
    ? 'Organisation Detail'
    : title;

  const finalSub = isEdit
    ? 'Update organisation information'
    : sub;

  return (
    <div className="admin-shell">

      <Sidebar />

      <div className="admin-main">

        {/* HEADER */}

        <header className="admin-header">

          <div>

            <div className="admin-header-title">
              {finalTitle}
            </div>

            {finalSub && (
              <div className="admin-header-sub">
                {finalSub}
              </div>
            )}

          </div>

          <div className="admin-header-right">

            {/* Notifications */}

            <Link
              to="/admin/contact"
              className="header-icon-btn"
              title="Notifications"
            >
              <Bell size={18} />
              <span className="notif-dot"></span>
            </Link>

            {/* User */}

            <div style={{ position: 'relative' }}>

              <div
                onClick={() => setMenuOpen((o) => !o)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 10px',
                  background: 'var(--gray-100)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer'
                }}
              >

                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    background: 'var(--green)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.72rem'
                  }}
                >
                  {(user?.full_name || 'ZA')
                    .split(' ')
                    .map((w) => w[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </div>

                <div>

                  <div
                    style={{
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: 'var(--gray-800)',
                      lineHeight: 1.2
                    }}
                  >
                    {user?.full_name}
                  </div>

                  <div
                    style={{
                      fontSize: '0.67rem',
                      color: 'var(--gray-500)'
                    }}
                  >
                    {user?.email}
                  </div>

                </div>

                <ChevronDown size={16} />

              </div>

              {menuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '110%',
                    right: 0,
                    background: 'white',
                    border: '1px solid var(--gray-200)',
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    minWidth: 160,
                    zIndex: 200,
                    overflow: 'hidden'
                  }}
                >
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      logout();
                      navigate('/admin/login');
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 14px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.82rem',
                      color: 'var(--crimson)',
                      textAlign: 'left'
                    }}
                  >
                    <LogOut size={15} /> Logout
                  </button>
                </div>
              )}

            </div>

          </div>

        </header>

        {/* PAGE */}

        <main className="admin-content">
          {children}
        </main>

      </div>

    </div>
  );
}